/**
 * CalendarDay — the Day view of the events calendar (route `calendar.day`).
 *
 * #806 (2026-07-13 docket §3): completes the Calendar migration. UIR-24
 * (2026-07-25): data source is the typed `getCalendar` range op, windowed to
 * the visible day — unified entries AND availability blocks (see cal-range.ts).
 * Same type-filter + view-switcher chrome as Week, one wide day column.
 * Reached from the Week view's "Day" button and its day headers
 * (params: day / month / year), or directly (defaults to today).
 */
import { useState, useMemo, useEffect, useRef } from "react";
import { SegmentedControl, alpha, Btn, Checkbox, Icon, IconBtn, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "../events/eventSectionCounts";
import { loadCalendarRange, blockSpanForDay, isScopeForbidden, type CalRangeBlock } from "./cal-range";
import { useT } from "../../lib/i18n";
import { currentCalendarDate, friendlyCalendarDay, friendlyCalendarHour, friendlyCalendarTime, toParts } from "../../lib/datetime";

interface CalEvent { id:string; title:string; start:number; end:number; color:string; type:string; loc?:string; }

const HOURS = [8,9,10,11,12,13,14,15,16,17,18,19,20]; // ui-config
const ROW_H = 56;
const TIME_W = 60;

export default function CalendarDay() {
  const t = useT();
  const nav = useNav();
  // B2 (2026-07-22 docket): "today" and the now-line follow the tenant display
  // tz, not the browser's local zone — otherwise the day view highlights the
  // wrong day and positions the now-line an hour off near a tz/day boundary.
  const today = currentCalendarDate();
  const nowP = toParts(new Date()) ?? { y: today.y, mo: today.mo, d: today.d, h: 0, mi: 0 };
  // Initial day from router params (Week's Day button passes {day}; its day
  // headers pass {day, month, year}); missing pieces default to today.
  const p = (nav.params || {}) as { day?: string | number; month?: string | number; year?: string | number };
  const initial = useMemo(() => new Date(
    p.year != null ? Number(p.year) : today.y,
    p.month != null ? Number(p.month) : today.mo,
    p.day != null ? Number(p.day) : today.d,
  ), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [dayOffset, setDayOffset] = useState(0);
  const day = useMemo(() => { const d = new Date(initial); d.setDate(d.getDate() + dayOffset); return d; }, [initial, dayOffset]);
  const isToday = day.getFullYear()===today.y && day.getMonth()===today.mo && day.getDate()===today.d;

  const [calEvents, setCalEvents] = useState<CalEvent[] | null>(null);
  // UIR-24 (Voyage #249): availability blocks + honest load-error state.
  const [calBlocks, setCalBlocks] = useState<CalRangeBlock[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  // The filter's universe of types is derived straight from the events
  // actually on screen — never a hardcoded/static allowlist. A static list
  // can silently drift from the real category labels the API returns (that
  // was the bug: an event's category could never appear in the filter, so it
  // stayed permanently hidden, and "Clear" — which only reset to the same
  // stale allowlist — could never surface it again).
  const CAL_TYPES = useMemo(() => {
    const seen = new Map<string, string>(); // category label -> swatch colour (first event wins)
    for (const e of calEvents ?? []) if (!seen.has(e.type)) seen.set(e.type, e.color);
    return Array.from(seen, ([id, color]) => ({ id, label: id, color }));
  }, [calEvents]);
  const [activeTypes, setActiveTypes] = useState<Set<string>>(() => new Set(CAL_TYPES.map((t)=>t.id)));
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filterOpen) return;
    const h = (e: MouseEvent) => { if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterOpen(false); };
    document.addEventListener("mousedown",h);
    return () => document.removeEventListener("mousedown",h);
  },[filterOpen]);

  // Whenever a category appears that isn't in activeTypes yet (events load,
  // or a later fetch surfaces a label not seen before), default it to
  // visible — that gap is exactly what made "Clear" a no-op.
  useEffect(() => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const t of CAL_TYPES) if (!next.has(t.id)) { next.add(t.id); changed = true; }
      return changed ? next : prev;
    });
  }, [CAL_TYPES]);

  useEffect(() => {
    const from = new Date(day); from.setHours(0,0,0,0);
    const to = new Date(day); to.setHours(24,0,0,0);
    let live = true;
    // UIR-24 (Voyage #249): typed calendar op — events AND availability blocks.
    loadCalendarRange(from, to)
      .then(({ events, blocks }) => {
        if (!live) return;
        setLoadError(null);
        setCalEvents(events
          .filter((e) => {
            const d0 = e.startsAt ? new Date(e.startsAt) : null;
            if (!d0 || isNaN(d0.getTime())) return true;
            // Day membership decided in the TENANT calendar date (toParts),
            // never raw UTC y/m/d -- an event at 23:00 America/New_York
            // (03:00Z the next day) belongs on THIS day, not tomorrow.
            const p0 = toParts(d0);
            return !!p0 && p0.y === day.getFullYear() && p0.mo === day.getMonth() && p0.d === day.getDate();
          })
          .map((e): CalEvent => {
            const sd = e.startsAt ? new Date(e.startsAt) : null;
            const ed = e.endsAt ? new Date(e.endsAt) : sd;
            // Position + label in the TENANT display tz (toParts), never the
            // browser's local zone (was sd.getHours()) -- the calendar-day
            // false-pass this closes (10:00 AM chip sitting in the 14:00 row).
            const sp = sd ? toParts(sd) : null;
            const ep = ed ? toParts(ed) : null;
            return {
              id: e.id, title: e.title,
              start: sp ? sp.h + sp.mi/60 : 9,
              end: ep ? ep.h + ep.mi/60 : 10,
              color: e.color, type: e.type, loc: e.loc,
            };
          }));
        setCalBlocks(blocks);
      })
      .catch((err) => {
        if (!live) return;
        console.warn("getCalendar failed:", err);
        setCalEvents([]); setCalBlocks([]);
        setLoadError(isScopeForbidden(err)
          ? t("calendar.day.scopeForbidden", "Your role's scope doesn't cover this calendar range (SCOPE_FORBIDDEN) — nothing is shown.")
          : t("calendar.day.couldnTLoadTheCalendar", "Couldn't load the calendar. Retry in a moment."));
      });
    return () => { live = false; };
  }, [day.getTime(), nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = (calEvents ?? []).filter((e)=> activeTypes.has(e.type));
  const hiddenCount = (calEvents ?? []).length - visible.length;
  const dateLabel = friendlyCalendarDay(day);


  return (
    <Screen rail="events" tabs={eventsSectionTabs(nav,"calendar",useEventSectionCounts())} activeTab="events">
      {/* Toolbar */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 24px", background:T.white, borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Btn variant="secondary" size="sm" onClick={() => nav.go("events")} icon={<Icon.chev dir="left" size={12}/>}>{t("calendar.day.events", "Events")}</Btn>
          <Btn variant="secondary" size="sm" onClick={() => { const d = new Date(initial); const diffDays = Math.round((new Date(today.y,today.mo,today.d).getTime() - new Date(d.getFullYear(),d.getMonth(),d.getDate()).getTime())/86400000); setDayOffset(diffDays); }}>{t("calendar.day.today", "Today")}</Btn>
          <IconBtn label={t("calendar.day.previousDay", "Previous day")} onClick={() => setDayOffset(dayOffset-1)}><Icon.chev dir="left"/></IconBtn>
          <IconBtn label={t("calendar.day.nextDay", "Next day")} onClick={() => setDayOffset(dayOffset+1)}><Icon.chev dir="right"/></IconBtn>
          <span style={{ fontSize:16, fontWeight:600, color:T.n1, display:"inline-flex", alignItems:"center", gap:8 }}>
            {dateLabel}
            {isToday ? <Pill tone="primary">{t("calendar.day.today", "Today")}</Pill> : null}
          </span>
          {hiddenCount > 0 ? <Pill tone="warn"><Icon.filter size={10}/> {hiddenCount} {t("calendar.day.hidden", "hidden ·")}<span style={{ textDecoration:"underline", cursor:"pointer" }} onClick={() => setActiveTypes(new Set(CAL_TYPES.map((t)=>t.id)))}>{t("common.actions.clear", "Clear")}</span></Pill> : null}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <div ref={filterRef} style={{ position:"relative" }}>
            <Btn variant="secondary" size="sm" icon={<Icon.filter size={14}/>} onClick={() => setFilterOpen((o)=>!o)}>
              {t("calendar.day.types", "Types ·")}{CAL_TYPES.filter((t) => activeTypes.has(t.id)).length}/{CAL_TYPES.length}
            </Btn>
            {filterOpen ? (
              <div style={{ position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:50, background:T.white, border:`1px solid ${T.border}`, borderRadius:8, boxShadow:T.shadowLg, padding:6, minWidth:200 }}>
                <div style={{ padding:"6px 10px 8px", fontSize:11, color:T.soft, fontWeight:600, textTransform:"uppercase", letterSpacing:0.04 }}>{t("calendar.day.eventTypes", "Event types")}</div>
                {CAL_TYPES.length === 0 ? <div style={{ padding:"8px 10px", fontSize:12.5, color:T.soft }}>{t("calendar.day.noEventsThisDayYet", "No events this day yet.")}</div> : null}
                {CAL_TYPES.map((t) => (
                  <label key={t.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:4, cursor:"pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background=T.n6)}
                    onMouseLeave={(e) => (e.currentTarget.style.background="transparent")}>
                    <Checkbox checked={activeTypes.has(t.id)} onChange={() => { const s=new Set(activeTypes); s.has(t.id)?s.delete(t.id):s.add(t.id); setActiveTypes(s); }}/>
                    <span style={{ width:10, height:10, borderRadius:3, background:t.color, flexShrink:0 }}/>
                    <span style={{ fontSize:13, color:T.n1 }}>{t.label}</span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
          <IconBtn label={t("common.actions.refresh", "Refresh")} onClick={() => { setCalEvents(null); setNonce((n) => n + 1); }}><Icon.refresh/></IconBtn>
          <IconBtn label={t("calendar.day.settings", "Settings")} onClick={() => nav.go("settings.calendar")}><Icon.settings/></IconBtn>
          <SegmentedControl<"day" | "week" | "month">
            size="md"
            ariaLabel="Calendar view"
            value="day"
            onChange={(v) => {  if (v === "week") nav.go("calendar.week"); if (v === "month") nav.go("calendar.month"); }}
            /* The three calendar views are configuration, not data. */
            options={[
              { value: "day", label: t("calendar.day.day", "Day") },
              { value: "week", label: t("calendar.day.week", "Week") },
              { value: "month", label: t("calendar.day.month", "Month") },
            ]}
          />
        </div>
      </div>

      {loadError ? <div style={{ padding:"8px 24px", background:alpha(T.red1,"12"), borderBottom:`1px solid ${alpha(T.red1,"33")}`, color:T.red1, fontSize:12.5, fontWeight:600, flexShrink:0 }}>{loadError}</div> : null}

      {/* Day grid */}
      <div style={{ flex:1, overflow:"auto", background:T.white }}>
        {/* All-day strip */}
        <div style={{ display:"grid", gridTemplateColumns:`${TIME_W}px 1fr`, borderBottom:`1px solid ${T.border}`, background:T.n6 }}>
          <div style={{ padding:"6px 8px", textAlign:"right", fontSize:10, color:T.soft, textTransform:"uppercase", letterSpacing:0.04 }}>{t("calendar.day.allDay", "All day")}</div>
          <div style={{ minHeight:28, borderLeft:`1px solid ${T.border}`, padding:4 }}/>
        </div>

        {/* Time grid */}
        <div style={{ display:"grid", gridTemplateColumns:`${TIME_W}px 1fr`, position:"relative" }}>
          <div style={{ borderRight:`1px solid ${T.border}` }}>
            {HOURS.map((h) => (
              <div key={h} style={{ height:ROW_H, paddingTop:4, paddingRight:8, textAlign:"right", fontSize:11, color:T.soft, fontVariantNumeric:"tabular-nums" }}>
                {friendlyCalendarHour(h)}
              </div>
            ))}
          </div>
          <div style={{ position:"relative", borderLeft:`1px solid ${T.border}` }}>
            {HOURS.map((h) => <div key={h} style={{ height:ROW_H, borderBottom:`1px solid ${alpha(T.border,"80")}` }}/>)}
            {calBlocks
              .map((b) => ({ b, span: blockSpanForDay(b, day, HOURS[0], HOURS[HOURS.length-1]+1) }))
              .filter((x): x is { b: CalRangeBlock; span: { startH: number; endH: number } } => x.span !== null)
              .map(({ b, span }) => {
                const top = (span.startH - HOURS[0]) * ROW_H;
                const height = (span.endH - span.startH) * ROW_H;
                const who = b.scope === "all-staff" ? t("calendar.day.allStaff", "All staff") : (b.staffName || t("calendar.day.staff", "Staff"));
                return (
                  <div key={b.id} title={/* TODO(i18n): replace template-literal with t('calendar.day.title', { ... }) */`${b.title} · ${who}${b.blockType ? ` · ${b.blockType}` : ""}`}
                    style={{ position:"absolute", left:4, right:4, top, height:Math.max(height-2,20), borderRadius:4, background:`repeating-linear-gradient(-45deg, ${alpha(T.n3,"2E")} 0px, ${alpha(T.n3,"2E")} 5px, ${alpha(T.n3,"14")} 5px, ${alpha(T.n3,"14")} 10px)`, border:`1px dashed ${T.n4}`, padding:"4px 9px", overflow:"hidden", zIndex:1 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:T.n2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{b.title}</div>
                    {height > 36 ? <div style={{ fontSize:11, color:T.soft, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{who}{b.blockType ? /* TODO(i18n): replace template-literal with t('calendar.day.text', { ... }) */` · ${b.blockType}` : ""}</div> : null}
                  </div>
                );
              })}
            {/* Now line — only on today's date, only within the visible hours */}
            {isToday && nowP.h >= HOURS[0] && nowP.h <= HOURS[HOURS.length-1] ? (
              <div style={{ position:"absolute", left:0, right:0, top:(nowP.h + nowP.mi/60 - HOURS[0]) * ROW_H, height:2, background:T.p1, zIndex:3 }}>
                <span style={{ position:"absolute", left:-4, top:-3, width:8, height:8, borderRadius:999, background:T.p1 }}/>
              </div>
            ) : null}
            {visible.map((e) => {
              const top = (e.start - HOURS[0]) * ROW_H;
              const height = (e.end - e.start) * ROW_H;
              return (
                <div key={e.id} onClick={() => toast({ tone:"info", title:e.title, subtitle:t("calendar.day.fmthmEStartFmthmEEndELocELoc.icu", { start: friendlyCalendarTime(e.start), end: friendlyCalendarTime(e.end), location: e.loc, hasLocation: e.loc ? "yes" : "no" })})}
                  style={{ position:"absolute", left:8, right:8, top, height:Math.max(height-2,24), borderRadius:6, background:alpha(e.color,"22"), borderLeft:`3px solid ${e.color}`, padding:"5px 10px", cursor:"pointer", overflow:"hidden", zIndex:2 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:e.color, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{e.title}</div>
                  {height > 34 ? <div style={{ fontSize:11.5, color:T.soft }}>{friendlyCalendarTime(e.start)} – {friendlyCalendarTime(e.end)}{e.loc ? /* TODO(i18n): replace template-literal with t('calendar.day.text2', { ... }) */` · ${e.loc}` : ""}</div> : null}
                </div>
              );
            })}
            {calEvents !== null && visible.length === 0 ? (
              <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
                <span style={{ fontSize:13, color:T.soft, background:T.white, padding:"8px 14px", borderRadius:8, border:`1px solid ${T.border}` }}>{t("calendar.day.noEventsScheduledThisDay", "No events scheduled this day.")}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Screen>
  );
}
